/**
 * Booking Page Configuration Service
 * Manages booking page settings (Family Saver, URLs, etc.)
 */

import config from '../config/environment';

// Always use live WordPress API for page configs (even when testing on localhost)
// This allows managing pages on the live server from local development
const LIVE_API_ENDPOINT = 'https://www.belvoircastle.com/wp/wp-json';
const API_BASE = `${LIVE_API_ENDPOINT}/custom/v1`;

// Log API base for debugging
if (process.env.NODE_ENV === 'development') {
  console.log('🔗 [PAGE-CONFIG] Using LIVE API endpoint:', API_BASE);
}

/**
 * Get all booking page configurations
 */
export const getAllPageConfigs = async () => {
  try {
    const url = `${API_BASE}/page-configs`;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔗 [PAGE-CONFIG] Fetching from:', url);
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [PAGE-CONFIG] API Error:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    // API returns { success: true, data: [...] }
    if (data.success && Array.isArray(data.data)) {
      return data.data;
    }
    
    // Fallback for different response structure
    if (Array.isArray(data)) {
      return data;
    }
    
    if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    
    // If no data found, return empty array
    console.warn('⚠️ [PAGE-CONFIG] Unexpected response structure:', data);
    return [];
  } catch (error) {
    console.error('❌ [PAGE-CONFIG] Failed to fetch page configs:', error);
    
    // If it's a connection error, provide helpful message and return empty array
    if (error.message.includes('Failed to fetch') || 
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('NetworkError')) {
      console.warn('⚠️ [PAGE-CONFIG] Connection error. WordPress may not be running.');
      console.warn('⚠️ [PAGE-CONFIG] Expected API endpoint:', `${API_BASE}/page-configs`);
      console.warn('⚠️ [PAGE-CONFIG] Please ensure WordPress is running and accessible.');
      // Return empty array instead of throwing to prevent UI crash
      return [];
    }
    
    throw error;
  }
};

/**
 * Get page configuration by page ID (for booking journey)
 */
export const getPageConfigByPageId = async (pageId) => {
  try {
    const response = await fetch(`${API_BASE}/page-config/${pageId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || null;
  } catch (error) {
    console.error(`❌ [PAGE-CONFIG] Failed to fetch config for page ${pageId}:`, error);
    return null; // Return null instead of throwing - page might not be configured yet
  }
};

/**
 * Create new page configuration
 */
export const createPageConfig = async (configData) => {
  try {
    const url = `${API_BASE}/page-configs`;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔗 [PAGE-CONFIG] Creating page config at:', url);
      console.log('📦 [PAGE-CONFIG] Data:', configData);
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(configData),
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        // If response isn't JSON, use status text
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('✅ [PAGE-CONFIG] Created page configuration:', data);
    return data;
  } catch (error) {
    console.error('❌ [PAGE-CONFIG] Failed to create page config:', error);
    
    // Provide helpful error message for connection issues
    if (error.message.includes('Failed to fetch') || 
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('NetworkError')) {
      throw new Error('Cannot connect to WordPress API. Please check your internet connection and ensure the live server is accessible.');
    }
    
    throw error;
  }
};

/**
 * Update existing page configuration
 */
export const updatePageConfig = async (id, configData) => {
  try {
    const url = `${API_BASE}/page-configs/${id}`;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔗 [PAGE-CONFIG] Updating page config at:', url);
    }
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(configData),
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        // If response isn't JSON, use status text
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('✅ [PAGE-CONFIG] Updated page configuration:', data);
    return data;
  } catch (error) {
    console.error('❌ [PAGE-CONFIG] Failed to update page config:', error);
    
    if (error.message.includes('Failed to fetch') || 
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('NetworkError')) {
      throw new Error('Cannot connect to WordPress API. Please check your internet connection.');
    }
    
    throw error;
  }
};

/**
 * Delete page configuration
 */
export const deletePageConfig = async (id) => {
  try {
    const url = `${API_BASE}/page-configs/${id}`;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔗 [PAGE-CONFIG] Deleting page config at:', url);
    }
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        // If response isn't JSON, use status text
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('✅ [PAGE-CONFIG] Deleted page configuration:', data);
    return data;
  } catch (error) {
    console.error('❌ [PAGE-CONFIG] Failed to delete page config:', error);
    
    if (error.message.includes('Failed to fetch') || 
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('NetworkError')) {
      throw new Error('Cannot connect to WordPress API. Please check your internet connection.');
    }
    
    throw error;
  }
};

/**
 * Generate booking URL for a page
 */
export const generateBookingUrl = (locationId, pageId, baseUrl = config.frontendUrl) => {
  return `${baseUrl}/booking-journey?locationId=${locationId}&pageId=${pageId}`;
};

